import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Eye, Users, Repeat, Clock, MapPin, Monitor, Smartphone, Tablet, Globe } from "lucide-react";
import { api } from "@/lib/site";

const fmtDur = (s) => {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

const deviceIcon = (d) =>
  d === "Mobile" ? Smartphone : d === "Tablet" ? Tablet : Monitor;

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/analytics/summary"), api.get("/analytics/events")])
      .then(([a, b]) => {
        setData(a.data);
        setEvents(b.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-[#A19CA8] mb-10">Loading analytics…</p>;
  if (!data) return null;

  const stats = [
    { label: "Total Visits", value: data.total_visits, icon: Eye },
    { label: "Unique Visitors", value: data.unique_visitors, icon: Users },
    { label: "Repeat Visitors", value: data.returning_visitors, icon: Repeat },
    { label: "Avg. Time on Site", value: fmtDur(data.avg_duration), icon: Clock },
  ];

  const chartData = data.over_time.map((o) => ({ ...o, label: o.day.slice(5) }));

  return (
    <div className="mb-12" data-testid="analytics-panel">
      <h2 className="font-display text-2xl text-[#D4AF37] mb-4">Website Analytics</h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((s) => (
          <div key={s.label} data-testid={`analytics-${s.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-[#A19CA8]">{s.label}</p>
              <s.icon className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="font-display text-3xl gold-text font-bold mt-2">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-5 mb-6">
        <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4">Visits Over Time</p>
        {chartData.length === 0 ? (
          <p className="text-[#A19CA8] text-sm">No visits recorded yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.12)" vertical={false} />
              <XAxis dataKey="label" stroke="#A19CA8" fontSize={12} tickLine={false} />
              <YAxis stroke="#A19CA8" fontSize={12} allowDecimals={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#06040A", border: "1px solid rgba(212,175,55,0.4)", borderRadius: 0, color: "#F9F6F0" }}
                cursor={{ fill: "rgba(212,175,55,0.08)" }}
              />
              <Bar dataKey="visits" fill="#D4AF37" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <ListCard title="Top Sections Viewed" icon={Eye} items={data.sections} nameKey="section" />
        <ListCard
          title="Locations"
          icon={MapPin}
          items={data.locations.map((l) => ({ name: l.city ? `${l.city}, ${l.country}` : l.country, count: l.count }))}
          nameKey="name"
        />
        <ListCard title="Devices" icon={Globe} items={data.devices} nameKey="device" />
      </div>

      <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-5">
        <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4">Recent Visits</p>
        {events.length === 0 ? (
          <p className="text-[#A19CA8] text-sm">No visits yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-visits-table">
              <thead>
                <tr className="text-[#A19CA8] uppercase text-xs tracking-wider border-b border-[#D4AF37]/15">
                  <th className="text-left py-2 pr-4 font-medium">When</th>
                  <th className="text-left py-2 pr-4 font-medium">New / Repeat</th>
                  <th className="text-left py-2 pr-4 font-medium">Location</th>
                  <th className="text-left py-2 pr-4 font-medium">Device</th>
                  <th className="text-left py-2 font-medium">Time on Site</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const Dev = deviceIcon(e.device);
                  return (
                    <tr key={e.id} data-testid="visit-row" className="border-b border-[#D4AF37]/8 text-[#F9F6F0]/90">
                      <td className="py-2.5 pr-4 whitespace-nowrap text-[#A19CA8]">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-xs px-2 py-0.5 border ${e.is_returning ? "border-[#D4AF37]/50 text-[#D4AF37]" : "border-[#A19CA8]/30 text-[#A19CA8]"}`}>
                          {e.is_returning ? "Repeat" : "New"}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        {e.city ? `${e.city}, ${e.country}` : e.country || "Unknown"}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className="flex items-center gap-1.5 text-[#A19CA8]">
                          <Dev className="h-4 w-4 text-[#D4AF37]" /> {e.device} · {e.browser}
                        </span>
                      </td>
                      <td className="py-2.5 text-[#A19CA8]">{fmtDur(e.duration_seconds)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const ListCard = ({ title, icon: Icon, items, nameKey }) => {
  const max = items.length ? Math.max(...items.map((i) => i.count)) : 1;
  return (
    <div className="bg-[#130A1F]/70 border border-[#D4AF37]/25 p-5">
      <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4" /> {title}
      </p>
      {items.length === 0 ? (
        <p className="text-[#A19CA8] text-sm">No data yet.</p>
      ) : (
        <ul className="space-y-3">
          {items.slice(0, 8).map((it, idx) => (
            <li key={idx}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-[#F9F6F0]/90 truncate pr-2">{it[nameKey] || "Unknown"}</span>
                <span className="text-[#D4AF37] font-semibold shrink-0">{it.count}</span>
              </div>
              <div className="h-1.5 w-full bg-[#06040A] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F3D56D]" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
